import dayjs from "dayjs";
import { ahoraChile } from "./fechaChile";

/**
 * vigenciaRegular.ts — estado de vigencia de un flujo REGULAR, para la UI.
 *
 * Espejo frontend de `estadoVigenciaRegular` (backend `utils/vigenciaRegular.js`).
 *
 * POR QUÉ ESTÁ ACÁ Y NO EN CADA PÁGINA: la misma vigencia se muestra en
 * `/flujos` (round 2026-07-26) y en `/sueldos` (round 2026-07-28), y son la
 * MISMA fila de la tabla `gastos` vista desde dos módulos. Dos copias del
 * cálculo terminan divergiendo, y el día que diverjan un mismo registro va a
 * decir "Vigente" en una pantalla y "Terminado" en la otra.
 *
 * GRANULARIDAD: MES, igual que el backend. Un regular se paga un día fijo del
 * mes, así que "vigente desde el 15" no tendría respuesta clara para ese mes.
 */

export type EstadoVigencia = "vigente" | "programado" | "terminado" | "sin_vigencia";

/** Lo mínimo que necesita un registro para que se le calcule el estado. */
export interface ConVigencia {
  vigenciaInicio?: string | null;
  vigenciaFin?: string | null;
}

/**
 * Estado del flujo regular respecto de hoy.
 *
 * `sin_vigencia` no es "uno más de la lista": son las filas creadas antes de
 * que la vigencia existiera. No tienen rango y por eso cuentan en TODOS los
 * meses. Se marcan aparte para que se puedan ir saldando — el sistema no les
 * inventa una fecha de inicio porque no hay de dónde sacarla (la tabla no
 * guarda fecha de creación).
 */
export function estadoVigencia(
  registro: ConVigencia,
  hoy: dayjs.Dayjs = ahoraChile(),
): EstadoVigencia {
  if (!registro.vigenciaInicio && !registro.vigenciaFin) return "sin_vigencia";
  if (
    registro.vigenciaInicio &&
    hoy.isBefore(dayjs(registro.vigenciaInicio), "month")
  ) {
    return "programado";
  }
  if (registro.vigenciaFin && hoy.isAfter(dayjs(registro.vigenciaFin), "month")) {
    return "terminado";
  }
  return "vigente";
}

/** Color y texto del tag por estado. Un solo lugar para las dos páginas. */
export const ESTADO_VIGENCIA_TAG: Record<
  EstadoVigencia,
  { color: string; texto: string }
> = {
  vigente: { color: "green", texto: "Vigente" },
  programado: { color: "blue", texto: "Programado" },
  terminado: { color: "default", texto: "Terminado" },
  sin_vigencia: { color: "orange", texto: "Sin vigencia" },
};

/** "05/2026 → indefinido" · "sin fechas — cuenta en todos los meses". */
export function rangoVigenciaLegible(registro: ConVigencia): string {
  if (!registro.vigenciaInicio && !registro.vigenciaFin) {
    return "sin fechas — cuenta en todos los meses";
  }
  const desde = registro.vigenciaInicio
    ? dayjs(registro.vigenciaInicio).format("MM/YYYY")
    : "?";
  const hasta = registro.vigenciaFin
    ? dayjs(registro.vigenciaFin).format("MM/YYYY")
    : "indefinido";
  return `${desde} → ${hasta}`;
}
